// src/components/InvoiceDownloadModal.jsx - FULL GST & ITR COMPLIANT EXPORT

import React, { useState } from 'react';
import { Download, Loader, Calendar, X } from 'lucide-react';
import StorageService from '../services/StorageService';

const InvoiceDownloadModal = ({ restaurantId, onClose }) => {
  const [period, setPeriod] = useState('1month');
  const [loading, setLoading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState('');

  const getPeriodDateRange = () => {
    const today = new Date();
    let startDate;

    switch (period) {
      case '1month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
        break;
      case '6months':
        startDate = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
        break;
      case '1year':
        startDate = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
        break;
      default:
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
    }

    return { startDate, endDate: today };
  };

  const generateGSTCompliantCSV = async (invoices, restaurant) => {
    // GST Registration Status
    const isGSTRegistered = restaurant.gstNo && restaurant.gstNo.trim().length > 0;
    const hsnCode = restaurant.hsnCode || '996331'; // Default HSN code for restaurant services
    
    // Report Header
    let csv = `GST & ITR COMPLIANT SALES REGISTER\n`;
    csv += `Restaurant Name: ${restaurant.name}\n`;
    csv += `Address: ${restaurant.address || 'Not Set'}\n`;
    csv += `Phone: ${restaurant.phone || restaurant.ownerPhone || 'Not Set'}\n`;
    csv += `Email: ${restaurant.email || 'Not Set'}\n`;
    csv += `GST Registration Status: ${isGSTRegistered ? 'REGISTERED' : 'NOT REGISTERED'}\n`;
    if (isGSTRegistered) {
      csv += `GSTIN: ${restaurant.gstNo}\n`;
    }
    csv += `State: ${restaurant.state || 'Not Set'}\n`;
    csv += `Place of Supply: ${restaurant.state || 'Not Set'}\n`;
    csv += `Period: ${getPeriodDateRange().startDate.toLocaleDateString('en-IN')} to ${getPeriodDateRange().endDate.toLocaleDateString('en-IN')}\n`;
    csv += `Generated On: ${new Date().toLocaleString('en-IN')}\n`;
    csv += `Financial Year: ${getCurrentFinancialYear()}\n`;
    csv += `\n\n`;

    // GST Compliant Headers
    const headers = [
      'Invoice Number',
      'Invoice Date',
      'Invoice Time',
      'Financial Year',
      'Customer Name',
      'Table/Location',
      'HSN/SAC Code',
      'Place of Supply',
      'Invoice Type',
      'Reverse Charge',
      'Item Description',
      'Item Category',
      'Quantity',
      'Unit Price',
      'Taxable Value (Subtotal)',
      isGSTRegistered ? 'CGST Rate (%)' : 'Tax Rate (%)',
      isGSTRegistered ? 'CGST Amount' : 'Tax Amount',
      isGSTRegistered ? 'SGST Rate (%)' : '',
      isGSTRegistered ? 'SGST Amount' : '',
      'Service Charge Rate (%)',
      'Service Charge Amount',
      'Total Tax Amount',
      'Invoice Value (Grand Total)',
      'Payment Method',
      'Payment Status',
      'Invoice Status',
      'Printed',
      'Cancelled',
      'Cancellation Reason',
      'Cancelled Date',
      'Cancelled By',
      'Created By/Cashier',
      'Remarks'
    ].filter(h => h !== ''); // Remove empty headers for non-GST

    csv += headers.join(',') + '\n';

    // Invoice Data Rows
    invoices.forEach(inv => {
      const financialYear = getFinancialYear(new Date(inv.timestamp || inv.createdAt || inv.date));
      const placeOfSupply = restaurant.state || 'Not Set';
      const invoiceType = 'B2C'; // Business to Consumer (restaurants are typically B2C)
      const reverseCharge = 'No';
      
      // Combine items into description
      const itemsDescription = (inv.items || []).map(item => 
        `${item.name} (${item.quantity}x₹${item.price})`
      ).join(' | ') || 'No items';

      const itemCategories = (inv.items || []).map(item => 
        item.category || 'Food & Beverage'
      ).join(' | ') || 'Food & Beverage';

      const totalQuantity = (inv.items || []).reduce((sum, item) => sum + item.quantity, 0) || 1;
      const avgUnitPrice = (inv.subtotal || 0) / totalQuantity;

      const row = [
        inv.invoiceNumber,
        inv.date,
        inv.time,
        financialYear,
        inv.customerName || 'Walk-in Customer',
        inv.tableName || 'Counter',
        hsnCode,
        placeOfSupply,
        invoiceType,
        reverseCharge,
        `"${itemsDescription}"`,
        `"${itemCategories}"`,
        totalQuantity,
        avgUnitPrice.toFixed(2),
        inv.subtotal.toFixed(2),
        isGSTRegistered ? (inv.cgstPercent || 0) : (inv.cgstPercent + inv.sgstPercent || 0),
        isGSTRegistered ? inv.cgst.toFixed(2) : (inv.cgst + inv.sgst).toFixed(2),
        isGSTRegistered ? (inv.sgstPercent || 0) : '',
        isGSTRegistered ? inv.sgst.toFixed(2) : '',
        inv.servicePercent || 0,
        inv.serviceCharge.toFixed(2),
        inv.taxTotal.toFixed(2),
        inv.grandTotal.toFixed(2),
        inv.paymentMethod || 'Cash',
        inv.paymentStatus || 'Paid',
        inv.cancelled ? 'Cancelled' : (inv.status || 'Active'),
        inv.printed ? 'Yes' : 'No',
        inv.cancelled ? 'Yes' : 'No',
        inv.cancelled ? `"${inv.cancellationReason || 'N/A'}"` : 'N/A',
        inv.cancelled && inv.cancelledAt ? new Date(inv.cancelledAt).toLocaleString('en-IN') : 'N/A',
        inv.cancelled ? (inv.cancelledBy || 'N/A') : 'N/A',
        inv.cashier || 'System',
        inv.cancelled ? 'VOID - Not included in totals' : ''
      ].filter(cell => cell !== ''); // Remove empty cells for non-GST

      csv += row.join(',') + '\n';
    });

    // Summary Statistics (Active Invoices Only)
    const activeInvoices = invoices.filter(inv => !inv.cancelled);
    const cancelledInvoices = invoices.filter(inv => inv.cancelled);

    csv += `\n\nGST & TAX SUMMARY (ACTIVE INVOICES ONLY)\n`;
    csv += `Total Invoices,${invoices.length}\n`;
    csv += `Active Invoices,${activeInvoices.length}\n`;
    csv += `Cancelled Invoices,${cancelledInvoices.length}\n`;
    csv += `\nTAXABLE VALUE (SUBTOTAL)\n`;
    csv += `Total Taxable Value,₹${activeInvoices.reduce((sum, inv) => sum + inv.subtotal, 0).toFixed(2)}\n`;
    
    if (isGSTRegistered) {
      csv += `\nGST BREAKDOWN\n`;
      csv += `Total CGST Collected,₹${activeInvoices.reduce((sum, inv) => sum + inv.cgst, 0).toFixed(2)}\n`;
      csv += `Total SGST Collected,₹${activeInvoices.reduce((sum, inv) => sum + inv.sgst, 0).toFixed(2)}\n`;
      csv += `Total GST Collected,₹${activeInvoices.reduce((sum, inv) => sum + inv.cgst + inv.sgst, 0).toFixed(2)}\n`;
    } else {
      csv += `\nTAX BREAKDOWN (Non-GST)\n`;
      csv += `Total Tax Collected,₹${activeInvoices.reduce((sum, inv) => sum + inv.cgst + inv.sgst, 0).toFixed(2)}\n`;
    }
    
    csv += `\nOTHER CHARGES\n`;
    csv += `Total Service Charge,₹${activeInvoices.reduce((sum, inv) => sum + inv.serviceCharge, 0).toFixed(2)}\n`;
    csv += `Total All Taxes & Charges,₹${activeInvoices.reduce((sum, inv) => sum + inv.taxTotal, 0).toFixed(2)}\n`;
    
    csv += `\nGRAND TOTAL (INVOICE VALUE)\n`;
    csv += `Total Sales Revenue,₹${activeInvoices.reduce((sum, inv) => sum + inv.grandTotal, 0).toFixed(2)}\n`;
    csv += `Average Invoice Value,₹${(activeInvoices.reduce((sum, inv) => sum + inv.grandTotal, 0) / activeInvoices.length).toFixed(2)}\n`;

    csv += `\nPRINT STATUS\n`;
    csv += `Printed Invoices,${activeInvoices.filter(inv => inv.printed).length}\n`;
    csv += `Pending Print,${activeInvoices.filter(inv => !inv.printed).length}\n`;

    // Payment Method Breakdown
    csv += `\nPAYMENT METHOD BREAKDOWN\n`;
    const paymentMethods = {};
    activeInvoices.forEach(inv => {
      const method = inv.paymentMethod || 'Cash';
      paymentMethods[method] = (paymentMethods[method] || 0) + inv.grandTotal;
    });
    Object.entries(paymentMethods).forEach(([method, amount]) => {
      csv += `${method},₹${amount.toFixed(2)}\n`;
    });

    // Cancelled Invoices Summary
    if (cancelledInvoices.length > 0) {
      csv += `\n\nCANCELLED INVOICES SUMMARY\n`;
      csv += `Total Cancelled,${cancelledInvoices.length}\n`;
      csv += `Cancelled Amount (Not in Revenue),₹${cancelledInvoices.reduce((sum, inv) => sum + inv.grandTotal, 0).toFixed(2)}\n`;
      
      csv += `\nCancellation Reasons:\n`;
      const reasonGroups = {};
      cancelledInvoices.forEach(inv => {
        const reason = inv.cancellationReason || 'Unknown';
        reasonGroups[reason] = (reasonGroups[reason] || 0) + 1;
      });
      Object.entries(reasonGroups).forEach(([reason, count]) => {
        csv += `"${reason}",${count}\n`;
      });
    }

    // HSN-wise Summary (Required for GST)
    if (isGSTRegistered) {
      csv += `\n\nHSN/SAC WISE SUMMARY (For GSTR-1)\n`;
      csv += `HSN/SAC Code,Description,Total Quantity,Taxable Value,CGST Amount,SGST Amount,Total Tax,Total Value\n`;
      csv += `${hsnCode},Restaurant Services,${activeInvoices.reduce((sum, inv) => sum + inv.totalItems, 0)},₹${activeInvoices.reduce((sum, inv) => sum + inv.subtotal, 0).toFixed(2)},₹${activeInvoices.reduce((sum, inv) => sum + inv.cgst, 0).toFixed(2)},₹${activeInvoices.reduce((sum, inv) => sum + inv.sgst, 0).toFixed(2)},₹${activeInvoices.reduce((sum, inv) => sum + inv.cgst + inv.sgst, 0).toFixed(2)},₹${activeInvoices.reduce((sum, inv) => sum + inv.grandTotal, 0).toFixed(2)}\n`;
    }

    // Compliance Notes
    csv += `\n\nCOMPLIANCE NOTES\n`;
    if (isGSTRegistered) {
      csv += `Status: GST Registered - File GSTR-1 and GSTR-3B monthly/quarterly\n`;
      csv += `GSTR-1: Report all B2C invoices in table 7 (consolidated)\n`;
      csv += `GSTR-3B: Report total tax collected and paid\n`;
      csv += `Maintain records for 6 years as per GST Act\n`;
      csv += `HSN Code 996331: Services of serving food and beverages in restaurants\n`;
    } else {
      csv += `Status: Not GST Registered (Turnover below ₹20 lakh)\n`;
      csv += `Use this data for ITR filing (Income Tax Return)\n`;
      csv += `File ITR-3 or ITR-4 (Presumptive Taxation) as applicable\n`;
      csv += `Maintain books of accounts and supporting documents\n`;
      csv += `Consider GST registration if approaching ₹20 lakh turnover\n`;
    }
    csv += `\nDocument Type: Sales Register\n`;
    csv += `Generated by: Restaurant Management System\n`;
    csv += `Format: CSV (Excel Compatible)\n`;
    csv += `\nIMPORTANT: Consult with a Chartered Accountant for tax filing\n`;

    return csv;
  };

  const getCurrentFinancialYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1; // 0-indexed
    
    if (month >= 4) {
      return `${year}-${(year + 1).toString().slice(-2)}`;
    } else {
      return `${year - 1}-${year.toString().slice(-2)}`;
    }
  };

  const getFinancialYear = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    
    if (month >= 4) {
      return `${year}-${(year + 1).toString().slice(-2)}`;
    } else {
      return `${year - 1}-${year.toString().slice(-2)}`;
    }
  };

  const downloadInvoices = async () => {
    try {
      setLoading(true);
      setDownloadProgress('Fetching restaurant details...');

      const restaurant = await StorageService.getRestaurant(restaurantId);
      
      setDownloadProgress('Fetching ALL invoices (including cancelled)...');
      
      // ✅ GET ALL INVOICES - Don't use date range filter that might exclude cancelled
      const allInvoices = await StorageService.getInvoices(restaurantId);
      
      // ✅ Apply date filter manually to include cancelled invoices
      const { startDate, endDate } = getPeriodDateRange();
      const invoices = allInvoices.filter(inv => {
        // Parse invoice date - handle multiple formats
        let invDate;
        if (inv.timestamp) {
          invDate = new Date(inv.timestamp);
        } else if (inv.createdAt) {
          invDate = new Date(inv.createdAt);
        } else if (inv.date && inv.time) {
          // Parse DD/MM/YYYY format
          const [day, month, year] = inv.date.split('/');
          invDate = new Date(`${year}-${month}-${day}T${inv.time}`);
        } else {
          invDate = new Date(inv.date);
        }
        
        return invDate >= startDate && invDate <= endDate;
      });

      if (invoices.length === 0) {
        alert('No invoices found for this period');
        setLoading(false);
        return;
      }
      
       

      // ✅ Sort invoices by invoice number (descending)
      invoices.sort((a, b) => {
        const numA = parseInt(a.invoiceNumber);
        const numB = parseInt(b.invoiceNumber);
        return numB - numA; // Descending order
      });

      setDownloadProgress(`Processing ${invoices.length} invoices for GST compliance...`);

      // Generate GST Compliant CSV
      const csvContent = await generateGSTCompliantCSV(invoices, restaurant);

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const periodName = {
        '1month': '1Month',
        '6months': '6Months',
        '1year': '1Year'
      }[period];

      const isGSTRegistered = restaurant.gstNo && restaurant.gstNo.trim().length > 0;
      const fileName = `Sales_Register_${isGSTRegistered ? 'GST' : 'ITR'}_${periodName}_${new Date().toISOString().split('T')[0]}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setDownloadProgress('');
      
      const activeCount = invoices.filter(inv => !inv.cancelled).length;
      const cancelledCount = invoices.filter(inv => inv.cancelled).length;
      const gstStatus = isGSTRegistered ? '✅ GST Registered' : '📋 ITR Compliant';
      
      alert(`✅ Downloaded ${invoices.length} invoices successfully!\n\n${gstStatus}\nActive: ${activeCount}\nCancelled: ${cancelledCount}\n\nFile: ${fileName}`);
      onClose();
    } catch (error) {
      console.error('Error downloading invoices:', error);
      alert('Failed to download invoices. Please try again.');
      setDownloadProgress('');
    } finally {
      setLoading(false);
    }
  };

  const getPeriodLabel = () => {
    switch (period) {
      case '1month':
        return 'Last 1 Month';
      case '6months':
        return 'Last 6 Months';
      case '1year':
        return 'Last 1 Year';
      default:
        return 'Last 1 Month';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Download className="w-6 h-6 text-orange-500" />
            Download Sales Register
          </h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Period Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Select Period for Download
            </label>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setPeriod('1month')}
                disabled={loading}
                className={`w-full py-3 px-4 rounded-lg border-2 transition text-left ${
                  period === '1month'
                    ? 'border-orange-500 bg-orange-50 text-orange-700 font-semibold'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <p className="font-medium">Last 1 Month</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {new Date(new Date().getFullYear(), new Date().getMonth() - 1, new Date().getDate()).toLocaleDateString('en-IN')} onwards
                </p>
              </button>

              <button
                type="button"
                onClick={() => setPeriod('6months')}
                disabled={loading}
                className={`w-full py-3 px-4 rounded-lg border-2 transition text-left ${
                  period === '6months'
                    ? 'border-orange-500 bg-orange-50 text-orange-700 font-semibold'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <p className="font-medium">Last 6 Months</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {new Date(new Date().getFullYear(), new Date().getMonth() - 6, new Date().getDate()).toLocaleDateString('en-IN')} onwards
                </p>
              </button>

              <button
                type="button"
                onClick={() => setPeriod('1year')}
                disabled={loading}
                className={`w-full py-3 px-4 rounded-lg border-2 transition text-left ${
                  period === '1year'
                    ? 'border-orange-500 bg-orange-50 text-orange-700 font-semibold'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                <p className="font-medium">Last 1 Year</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {new Date(new Date().getFullYear() - 1, new Date().getMonth(), new Date().getDate()).toLocaleDateString('en-IN')} onwards
                </p>
              </button>
            </div>
          </div>

          {/* Enhanced Info Box */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800 font-semibold mb-2">
              ✅ GST & ITR Compliant Sales Register
            </p>
            <ul className="text-xs text-gray-700 space-y-1">
              <li>✓ HSN/SAC Code: 996331 (Restaurant Services)</li>
              <li>✓ Complete tax breakdown (CGST, SGST, Service Charge)</li>
              <li>✓ Financial year-wise categorization</li>
              <li>✓ Active & cancelled invoices tracked separately</li>
              <li>✓ Payment method-wise summary</li>
              <li>✓ Ready for GSTR-1, GSTR-3B, or ITR filing</li>
            </ul>
          </div>

          {/* Progress Indicator */}
          {downloadProgress && (
            <div className="bg-gray-50 border border-gray-300 rounded-lg p-3">
              <p className="text-sm text-gray-700 flex items-center gap-2">
                <Loader className="w-4 h-4 animate-spin" />
                {downloadProgress}
              </p>
            </div>
          )}

          {/* Download Button */}
          <button
            onClick={downloadInvoices}
            disabled={loading}
            className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition shadow-md"
          >
            {loading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Generating Compliant Report...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Download {getPeriodLabel()} Sales Register
              </>
            )}
          </button>

          <button
            onClick={onClose}
            disabled={loading}
            className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 transition"
          >
            Cancel
          </button>

          {/* Compliance Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-xs text-yellow-800">
              <strong>⚠️ Important:</strong> This sales register is designed for GST & ITR compliance. For accurate tax filing, please consult with a Chartered Accountant.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDownloadModal;